import {concat, constant, emptyArray} from '@packages/utilities/fp/utils';
import {
  getInnermostSymbolInstance,
  getInnermostInstanceRootElement,
} from '@packages/utilities/platform';
import {weakMemo} from '@packages/utilities/memo';
import {
  Component,
  Expressions,
  matchExpression,
  Plugins,
  Types,
} from '@packages/systems/wfdl';
import {qualify} from '@packages/systems/wfdl-Qualified';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {TranslatedNodeData} from '@packages/systems/localization-types';
import type {NativeId} from '@packages/systems/core/utils/NativeIdUtils';
import {convertComponentPropertyDefaultToTranslatePayload} from './convertComponentPropertyDefaultToTranslatePayload';
import {getTranslatableComponentPropertiesOverrides} from './getTranslatableComponentPropertiesOverrides';

/**
 * The passed element should have at least one bound to a component
 * property otherwise just an empty array is returned.
 * @returns An array of objects with the default values and overrides
 * for the component properies the element may have.
 */
export const extractElementPropertiesTranslatableData = weakMemo(
  (components: Component.ComponentMap) =>
    weakMemo(
      (element: ExpressionElement) =>
        (selectedNativeIdPath: NativeId[]): TranslatedNodeData[] => {
          const componentInstance = getInnermostSymbolInstance(
            selectedNativeIdPath,
            components
          );

          const stop = Expressions.stop(emptyArray);
          const resume = Expressions.resume(emptyArray);
          const propsMatcher = matchExpression({
            Select: (select) => {
              // Check for property overrides.
              let propertyOverrides: TranslatedNodeData[] = [];

              if (componentInstance) {
                propertyOverrides = getTranslatableComponentPropertiesOverrides(
                  componentInstance,
                  [select.prop],
                  components
                );

                if (propertyOverrides.length > 0) {
                  // If there are overrides don't continue.
                  // getTranslatableComponentPropertiesOverrides already tags
                  // every item with componentId.
                  return Expressions.stop(propertyOverrides);
                }
              }

              // Get the property default values.
              const componentRoot = getInnermostInstanceRootElement(
                selectedNativeIdPath,
                components
              );

              if (componentRoot) {
                const internalComponent = components.get(
                  qualify(
                    Plugins.name('__SitePlugin'),
                    Component.name(componentRoot.id)
                  )
                );

                if (internalComponent) {
                  const dataType = Component.getDataType(internalComponent);
                  const fields = Types.getRecordFields(dataType);

                  if (fields) {
                    const type = fields[select.prop];

                    if (type) {
                      return Expressions.stop(
                        convertComponentPropertyDefaultToTranslatePayload(
                          select.prop,
                          componentRoot.id,
                          type
                        ).map((item) => ({
                          // Without this the exclusion gate resolves these to
                          // the page's canvas, so an excluded prop's default
                          // still gets translated. Overrides above already
                          // carry it.
                          ...item,
                          componentId: componentRoot.id,
                        }))
                      );
                    }
                  }
                }
              }

              return stop;
            },
            Element: constant(stop),
            _: constant(resume),
          });

          return Expressions.everything(concat)(propsMatcher)(
            Expressions.getElementData(element)
          );
        }
    )
);
